import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_BASE_URL } from '../../services/api';
import { Filter, User, Mail, CreditCard, Calendar, Search, ArrowLeft, CheckCircle, Clock, Download, FileSpreadsheet, FileText, CheckSquare, Square, X, Eye, Power } from 'lucide-react';
import { updatePaymentStatus, getPayment, approveMember, activateMember } from '../../services/api';
import toast from 'react-hot-toast';
const AdminPaymentsPage = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    memberName: '',
    memberEmail: '',
    orderId: '',
    status: '',
    paymentMethod: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [selectedPayments, setSelectedPayments] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ifm_admin_token');
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key] || params[key] === 'all') delete params[key];
      });
      
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        queryParams.append(key, params[key]);
      });
       const queryString = queryParams.toString();
       
       const url = `${DEFAULT_BASE_URL}/payments${queryString ? `?${queryString}` : ''}`;
       if (process.env.NODE_ENV === 'development') {
         console.log('Fetching payments from:', url);
       }
      
      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include' // Include credentials for CORS
      });
      
      if (!res.ok) {
        const errorText = await res.text().catch(() => 'Unknown error');
        console.error('Payment fetch failed:', res.status, errorText);
        throw new Error(`Failed to load payments: ${res.status} ${errorText}`);
      }
      const data = await res.json();
      
      // Handle response format
      let paymentsList = [];
      let paginationInfo = null;
      
      if (Array.isArray(data)) {
        paymentsList = data;
      } else if (data.payments) {
        paymentsList = data.payments;
        paginationInfo = {
          total: data.total || 0,
          totalPages: data.totalPages || data.totalPages || 0,
          page: data.currentPage || data.page || 1
        };
      } else {
        paymentsList = [];
      }
      
      setPayments(paymentsList);
      
      // Update pagination if data has pagination info
      if (paginationInfo) {
        setPagination(prev => ({
          ...prev,
          total: paginationInfo.total,
          totalPages: paginationInfo.totalPages
        }));
      }
      
      // Clear selections when data changes
      setSelectedPayments(new Set());
      setSelectAll(false);
    } catch (error) {
      console.error('Error loading payments:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'failed': return '#ef4444';
      case 'cancelled': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✅';
      case 'pending': return '⏳';
      case 'failed': return '❌';
      case 'cancelled': return '🚫';
      default: return '❓';
    }
  };

  // Payments are already filtered by backend
  const filteredPayments = payments;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(filteredPayments.map(p => p._id || p.orderId));
      setSelectedPayments(allIds);
      setSelectAll(true);
    } else {
      setSelectedPayments(new Set());
      setSelectAll(false);
    }
  };

  const handleSelectPayment = (paymentId) => {
    const newSelected = new Set(selectedPayments);
    if (newSelected.has(paymentId)) {
      newSelected.delete(paymentId);
    } else {
      newSelected.add(paymentId);
    }
    setSelectedPayments(newSelected);
    setSelectAll(newSelected.size === filteredPayments.length && filteredPayments.length > 0);
  };

  // Get data to export (selected or all)
  const getExportData = () => {
    if (selectedPayments.size > 0) {
      return filteredPayments.filter(p => selectedPayments.has(p._id || p.orderId));
    }
    return filteredPayments;
  };

  // Export to Excel
  const exportToExcel = async () => {
    try {
      // Dynamically import xlsx library
      const XLSX = await import('xlsx').catch(() => {
        // If xlsx is not available, use a fallback method
        alert('Excel export library not available. Please install xlsx: npm install xlsx');
        return null;
      });

      if (!XLSX) return;

      const dataToExport = getExportData();
      if (dataToExport.length === 0) {
        alert('No data to export');
        return;
      }

      // Prepare data for Excel
      const excelData = dataToExport.map(payment => ({
        'Order ID': payment.orderId || 'N/A',
        'Member Name': payment.memberId?.personName || payment.memberName || 'N/A',
        'Member Email': payment.memberId?.email || payment.memberEmail || 'N/A',
        'Member ID': payment.memberId?.customId || 'N/A',
        'Plan': payment.membershipPlanId?.name || payment.membershipPlanName || 'N/A',
        'Amount': payment.amount || 0,
        'Status': payment.status || 'N/A',
        'Date': formatDate(payment.createdAt),
        'Gateway': payment.gateway || 'Razorpay'
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Payments');

      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `payments_${dateStr}.xlsx`;

      // Write file
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export to Excel. Please try again.');
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      const dataToExport = getExportData();
      if (dataToExport.length === 0) {
        alert('No data to export');
        return;
      }

      // Calculate totals
      let totalAmount = 0;
      const completedCount = dataToExport.filter(p => p.status === 'completed').length;
      const pendingCount = dataToExport.filter(p => p.status === 'pending').length;
      dataToExport.forEach(payment => {
        if (payment.status === 'completed') {
          totalAmount += payment.amount || 0;
        }
      });

      // Create a well-formatted HTML table for PDF
      let htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Payments Report</title>
            <style>
              @page {
                size: A4;
                margin: 20mm;
              }
              @media print {
                html, body {
                  width: 210mm;
                  height: 297mm;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
                .page {
                  page-break-after: always;
                }
                .no-break-inside {
                  page-break-inside: avoid;
                }
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 0;
                color: #0f172a;
              }
              .page {
                width: 210mm;
                min-height: 297mm;
                margin: 0 auto;
                padding: 20mm 15mm;
                box-sizing: border-box;
              }
              h1 { color: #1e293b; margin-bottom: 10px; font-size: 24px; }
              h2 { color: #475569; margin-top: 20px; margin-bottom: 10px; font-size: 18px; }
              .header { margin-bottom: 25px; padding-bottom: 15px; border-bottom: 2px solid #cbd5e1; }
              .header p { margin: 5px 0; color: #64748b; }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 12px;
                font-size: 11px;
                table-layout: fixed;
              }
              th { background-color: #1e293b; color: white; padding: 12px 8px; text-align: left; border: 1px solid #334155; font-weight: bold; }
              td { padding: 10px 8px; border: 1px solid #cbd5e1; }
              tr:nth-child(even) { background-color: #f8fafc; }
              tr:hover { background-color: #f1f5f9; }
              .summary { margin-top: 20px; padding: 14px; background-color: #f1f5f9; border-radius: 8px; border-left: 4px solid #2563eb; }
              .summary h3 { margin-top: 0; color: #1e293b; }
              .summary p { margin: 8px 0; color: #475569; }
              .summary strong { color: #1e293b; }
              .status-completed { color: #059669; font-weight: bold; }
              .status-pending { color: #d97706; font-weight: bold; }
              .status-failed { color: #dc2626; font-weight: bold; }
              .status-cancelled { color: #6b7280; font-weight: bold; }
            </style>
          </head>
          <body>
            <div class="page">
            <div class="header no-break-inside">
              <h1>Payment Details Report</h1>
              <p><strong>Generated on:</strong> ${new Date().toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short' })}</p>
              <p><strong>Total Records:</strong> ${dataToExport.length} ${selectedPayments.size > 0 ? `(Selected: ${selectedPayments.size})` : '(All Payments)'}</p>
            </div>
            <table class="no-break-inside">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Member Name</th>
                  <th>Member Email</th>
                  <th>Member ID</th>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Gateway</th>
                </tr>
              </thead>
              <tbody>
      `;

      dataToExport.forEach(payment => {
        const amount = payment.amount || 0;
        const status = (payment.status || 'N/A').toUpperCase();
        const statusClass = `status-${payment.status || 'unknown'}`;
        
        htmlContent += `
          <tr>
            <td>${payment.orderId || 'N/A'}</td>
            <td>${(payment.memberId?.personName || payment.memberName || 'N/A').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
            <td>${(payment.memberId?.email || payment.memberEmail || 'N/A').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
            <td>${payment.memberId?.customId || 'N/A'}</td>
            <td>${(payment.membershipPlanId?.name || payment.membershipPlanName || 'N/A').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
            <td>${formatAmount(amount)}</td>
            <td class="${statusClass}">${status}</td>
            <td>${formatDate(payment.createdAt)}</td>
            <td>${payment.gateway || 'Razorpay'}</td>
          </tr>
        `;
      });

      htmlContent += `
              </tbody>
            </table>
            <div class="summary no-break-inside">
              <h3>Summary</h3>
              <p><strong>Total Records:</strong> ${dataToExport.length}</p>
              <p><strong>Completed Payments:</strong> ${completedCount}</p>
              <p><strong>Pending Payments:</strong> ${pendingCount}</p>
              <p><strong>Total Revenue (Completed):</strong> ${formatAmount(totalAmount)}</p>
            </div>
            </div>
          </body>
        </html>
      `;

      // Open print dialog for PDF
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      } else {
        // Fallback: Download as HTML if popup blocked
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `payments_${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        alert('Please use browser print (Ctrl+P) to save as PDF. HTML file downloaded as fallback.');
      }
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export to PDF. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/admin')} 
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{'Payments'}</h1>
              <p className="text-xs text-gray-500">View and manage all payment transactions</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900">{'Filters'}</h3>
            {Object.values(filters).some(f => f) && (
              <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                {Object.values(filters).filter(f => f).length} active
              </span>
            )}
          </div>
          {Object.values(filters).some(f => f) && (
            <button
              onClick={() => {
                setFilters({
                  memberName: '',
                  memberEmail: '',
                  orderId: '',
                  status: '',
                  paymentMethod: '',
                  startDate: '',
                  endDate: ''
                });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              {'Clear All'}
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-3">
          {/* Member Name Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">{'Member Name'}</label>
            <div className="relative">
              <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.memberName ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                placeholder="Search name..."
                value={filters.memberName}
                onChange={(e) => handleFilterChange('memberName', e.target.value)}
              />
              {filters.memberName && (
                <button
                  onClick={() => handleFilterChange('memberName', '')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Member Email Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">{'Email'}</label>
            <div className="relative">
              <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.memberEmail ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                placeholder="Search email..."
                value={filters.memberEmail}
                onChange={(e) => handleFilterChange('memberEmail', e.target.value)}
              />
              {filters.memberEmail && (
                <button
                  onClick={() => handleFilterChange('memberEmail', '')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Order ID Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">{'Order ID'}</label>
            <div className="relative">
              <CreditCard className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.orderId ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                placeholder="Search Order ID..."
                value={filters.orderId}
                onChange={(e) => handleFilterChange('orderId', e.target.value)}
              />
              {filters.orderId && (
                <button
                  onClick={() => handleFilterChange('orderId', '')}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  type="button"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">{'Status'}</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Payment Method Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">{'Payment Method'}</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              value={filters.paymentMethod}
              onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
            >
              <option value="">All Methods</option>
              <option value="online">Online</option>
              <option value="cash">Cash</option>
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">From Date</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">To Date</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              min={filters.startDate}
            />
          </div>
        </div>

      </div>

        {/* Payment Statistics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-sm border border-blue-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-blue-700 mb-0.5">Completed Payments</p>
                <p className="text-2xl font-bold text-blue-900">{payments.filter(p => p.status === 'completed').length}</p>
              </div>
              <div className="bg-blue-200 rounded-lg p-2">
                <CheckCircle className="h-4 w-4 text-blue-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg shadow-sm border border-amber-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-amber-700 mb-0.5">Pending Payments</p>
                <p className="text-2xl font-bold text-amber-900">{payments.filter(p => p.status === 'pending').length}</p>
              </div>
              <div className="bg-amber-200 rounded-lg p-2">
                <Clock className="h-4 w-4 text-amber-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-sm border border-green-200 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs font-medium text-green-700 mb-0.5">Total Revenue</p>
                <p className="text-2xl font-bold text-green-900">
                  {formatAmount(payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount || 0), 0))}
                </p>
              </div>
              <div className="bg-green-200 rounded-lg p-2">
                <CreditCard className="h-4 w-4 text-green-700" />
              </div>
            </div>
          </div>
        </div>

        {/* Payments List */}
        <div className="bg-white shadow-xl rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Payments</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {pagination.total || payments.length} payment{pagination.total !== 1 ? 's' : ''} total
                  {selectedPayments.size > 0 && (
                    <span className="ml-2 text-primary-600 font-semibold">
                      • {selectedPayments.size} selected
                    </span>
                  )}
                </p>
              </div>
              {filteredPayments.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportToExcel}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm"
                    title={selectedPayments.size > 0 ? `Export ${selectedPayments.size} selected payments to Excel` : 'Export all payments to Excel'}
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Export Excel
                  </button>
                  <button
                    onClick={exportToPDF}
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm"
                    title={selectedPayments.size > 0 ? `Export ${selectedPayments.size} selected payments to PDF` : 'Export all payments to PDF'}
                  >
                    <FileText className="h-4 w-4" />
                    Export PDF
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
              <p className="text-gray-600">{'Loading...'}</p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <CreditCard className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-2 font-medium">No payments found.</p>
              <p className="text-sm text-gray-500 mb-6">Try adjusting your filters</p>
              <button 
                onClick={() => {
                  setFilters({
                    memberName: '',
                    memberEmail: '',
                    orderId: '',
                    status: '',
                    paymentMethod: '',
                    startDate: '',
                    endDate: ''
                  });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all shadow-lg font-semibold"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200 w-12">
                      <input
                        type="checkbox"
                        checked={selectAll && filteredPayments.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        title="Select all"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Order ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Member</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Method</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPayments.map((payment, index) => {
                    const paymentId = payment._id || payment.orderId;
                    const isSelected = selectedPayments.has(paymentId);
                    return (
                      <tr key={paymentId || index} className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectPayment(paymentId)}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {payment.orderId || 'N/A'}
                        </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div>
                          <div className="font-medium text-gray-900">{payment.memberId?.personName || payment.memberName || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{payment.memberId?.email || payment.memberEmail || 'N/A'}</div>
                          {payment.memberId?.customId && (
                            <div className="text-xs text-primary-600 mt-1 font-semibold">ID: {payment.memberId.customId}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.membershipPlanId?.name || payment.membershipPlanName || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {formatAmount(payment.amount || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span 
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: `${getStatusColor(payment.status)}20`,
                            color: getStatusColor(payment.status)
                          }}
                        >
                          {getStatusIcon(payment.status)} {payment.status?.toUpperCase() || 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(payment.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                          payment.paymentMethod === 'cash' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {payment.paymentMethod === 'cash' ? 'Cash' : payment.gateway || 'Online'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={async () => {
                            setSelectedPayment(payment);
                            setShowViewModal(true);
                            setLoadingDetails(true);
                            try {
                              const details = await getPayment(payment._id);
                              setPaymentDetails(details);
                            } catch (error) {
                              console.error('Failed to load payment details:', error);
                              setPaymentDetails(null);
                            } finally {
                              setLoadingDetails(false);
                            }
                          }}
                          className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium transition-colors"
                        >
                          <Eye className="h-4 w-4" />
                          <span className="hidden lg:inline">{'View'}</span>
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between bg-white">
              <div className="text-sm text-gray-600">
                {'Page'} {pagination.page} {'of'} {pagination.totalPages} ({pagination.total || filteredPayments.length} {'total'})
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {'Previous'}
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {'Next'}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Payment View Modal */}
      {showViewModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch sm:items-center justify-end z-[1000]">
          <div className="bg-white shadow-xl w-full sm:w-[92vw] md:w-[82vw] lg:w-[70vw] xl:w-[60vw] 2xl:w-[52vw] max-w-[960px] h-full sm:h-[calc(100vh-32px)] sm:my-4 overflow-y-auto overflow-x-hidden rounded-none sm:rounded-2xl min-w-0">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">{'Payment Details'}</h2>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedPayment(null);
                  setPaymentDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-4 sm:px-6 py-4">
              {loadingDetails ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-4"></div>
                  <p className="text-gray-600">{'Loading...'}</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="text-xs font-medium text-gray-500">Order ID</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">{paymentDetails?.orderId || selectedPayment?.orderId || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Status</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        <span 
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: `${getStatusColor(paymentDetails?.status || selectedPayment?.status)}20`,
                            color: getStatusColor(paymentDetails?.status || selectedPayment?.status)
                          }}
                        >
                          {(paymentDetails?.status || selectedPayment?.status || 'UNKNOWN').toUpperCase()}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Amount</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {formatAmount(paymentDetails?.amount || selectedPayment?.amount || 0)}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Payment Method</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                          (paymentDetails?.paymentMethod || selectedPayment?.paymentMethod) === 'cash' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {(paymentDetails?.paymentMethod || selectedPayment?.paymentMethod) === 'cash' ? 'Cash' : 'Online'}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Member Name</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {paymentDetails?.memberId?.personName || selectedPayment?.memberId?.personName || paymentDetails?.memberName || selectedPayment?.memberName || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Member Email</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {paymentDetails?.memberId?.email || selectedPayment?.memberId?.email || paymentDetails?.memberEmail || selectedPayment?.memberEmail || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Member ID</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {paymentDetails?.memberId?.customId || selectedPayment?.memberId?.customId || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Plan</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {paymentDetails?.membershipPlanId?.name || selectedPayment?.membershipPlanId?.name || paymentDetails?.membershipPlanName || selectedPayment?.membershipPlanName || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Date</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {formatDate(paymentDetails?.createdAt || selectedPayment?.createdAt)}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Gateway</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {paymentDetails?.gateway || selectedPayment?.gateway || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Member Status Info */}
                  {paymentDetails?.memberId && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Member Status</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs font-medium text-gray-500">Approval Status</label>
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {paymentDetails.memberId.approvalStatus || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500">Membership Status</label>
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {paymentDetails.memberId.membershipStatus || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500">Payment Status</label>
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {paymentDetails.memberId.paymentStatus || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200 flex-wrap">
                    {/* Payment Status Actions */}
                    {(paymentDetails?.status || selectedPayment?.status) !== 'completed' && (
                      <button
                        onClick={async () => {
                          try {
                            await updatePaymentStatus(selectedPayment._id, 'completed', null, 
                              (paymentDetails?.paymentMethod || selectedPayment?.paymentMethod) || 'cash');
                            toast.success('Payment marked as completed');
                            // Reload payment details
                            const updated = await getPayment(selectedPayment._id);
                            setPaymentDetails(updated);
                            await loadPayments();
                          } catch (error) {
                            toast.error(error.message || 'Failed to update payment status');
                          }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-semibold w-full sm:w-auto justify-center"
                      >
                        <CheckCircle className="h-4 w-4" />
                        {'Mark Payment as Completed'}
                      </button>
                    )}

                    {/* Download Invoice (same invoice layout as payer email PDF) */}
                    {(paymentDetails?.status || selectedPayment?.status) === 'completed' && (
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('ifm_admin_token');
                            const baseUrl = DEFAULT_BASE_URL.replace(/\/$/, '');
                            const res = await fetch(`${baseUrl}/payments/${selectedPayment._id}/invoice-proof`, {
                              headers: {
                                'Authorization': `Bearer ${token}`,
                              },
                              credentials: 'include',
                            });
                            if (!res.ok) {
                              const text = await res.text().catch(() => '');
                              throw new Error(text || 'Failed to download invoice');
                            }
                            const blob = await res.blob();
                            const blobUrl = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.download = `invoice-${selectedPayment.orderId || selectedPayment._id}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(blobUrl);
                          } catch (error) {
                            console.error('Invoice download error:', error);
                            toast.error(error.message || 'Failed to download invoice');
                          }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-semibold w-full sm:w-auto justify-center"
                      >
                        <Download className="h-4 w-4" />
                        {'Download Invoice'}
                      </button>
                    )}

                    {/* Member Approval Actions */}
                    {paymentDetails?.memberId && (
                      <>
                        {paymentDetails.memberId.approvalStatus === 'Pending' && (
                          <button
                            onClick={async () => {
                              try {
                                await approveMember(paymentDetails.memberId._id, 'Admin', false);
                                toast.success('Member approved successfully');
                                // Reload payment details
                                const updated = await getPayment(selectedPayment._id);
                                setPaymentDetails(updated);
                                await loadPayments();
                              } catch (error) {
                                toast.error(error.message || 'Failed to approve member');
                              }
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold w-full sm:w-auto justify-center"
                          >
                            <CheckCircle className="h-4 w-4" />
                            {'Approve Member'}
                          </button>
                        )}
                        {paymentDetails.memberId.approvalStatus === 'Approved' && paymentDetails.memberId.membershipStatus !== 'Active' && (
                          <button
                            onClick={async () => {
                              try {
                                await activateMember(paymentDetails.memberId._id);
                                toast.success('Member activated successfully');
                                // Reload payment details
                                const updated = await getPayment(selectedPayment._id);
                                setPaymentDetails(updated);
                                await loadPayments();
                              } catch (error) {
                                toast.error(error.message || 'Failed to activate member');
                              }
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold w-full sm:w-auto justify-center"
                          >
                            <Power className="h-4 w-4" />
                            {'Activate Member'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentsPage;
